import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Card,
  Chip,
  Grid2 as Grid,
  Link,
  OutlinedInput,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import Viewer from "react-viewer";
import AssetIcon from "../../../../../Components/Misc/AssetIcon/asset-icon";
import AssetThumbnail from "../../../../../Components/Misc/AssetThumbnail";
import AssetClassChip from "../../../../../Components/Misc/Chips/AssetChip/asset-class-chip";
import AssetTypeFromObjectChip from "../../../../../Components/Misc/Chips/AssetChip/asset-type-chip-from-object";
import AssetStatus from "../../../../../Components/Misc/Status/AssetStatus/asset-status";
import { ModalContext } from "../../../../../Components/Modals";
import ModalPreview, { ModalPreviewProps } from "../../../../../Components/Modals/ModalPreview/modal-video-preview";
import AssetPanel from "../../../../../Components/Panels/AssetPanel/asset-panel";
import DataTable, { CellSize } from "../../../../../Components/Table/table";
import TableUuids from "../../../../../Components/Table/table-uuids";
import { TableCellLongTypography } from "../../../../../Components/Table/TableCellTypography/table-cell-typography";
import TableSkeleton from "../../../../../Components/Table/TableSkeleton";
import AssetTreeView from "../../../../../Components/TreeView/TitleTreeView/asset-tree-view-2";
import TreeViewActions from "../../../../../Components/TreeView/TreeViewActions/tree-view-actions";
import { cacheService, thumbnailService } from "../../../../../Core/Api/services";
import { ThumbnailSize } from "../../../../../Core/Api/Thumbnail/thumbnail-api-service";
import { DbMetadataFieldInfos } from "../../../../../Core/Api/Types/db-metadata-field-infos";
import { HierarchyTree } from "../../../../../Core/Api/Types/hierarchy-tree";
import { ObjectClass } from "../../../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../../../Core/Api/Types/object-layer";
import { ObjectStatus } from "../../../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../../../Core/Api/Types/object-type";
import { GetMetadataByTagName } from "../../../../../Core/Utils/asset-object-utils";
import { GetObjectHierarchyDto, IsObjectLoading } from "../../../../../Redux/Reducers/redux-slice-asset";
import { GetSelectedItems, TreeContext } from "../../../../../Redux/Reducers/redux-slice-tree";
import { useAppSelector } from "../../../../../Redux/redux-store";
import { routePaths } from "../../../../../Router/router";
import { Distinct } from "../../../../../Utils/helpers";
import useBasketActions from "../../../../../Utils/Hooks/useBasketActions";

type TabType = "tree" | "table";

function AssetSingleHierarchy() {
  const { t } = useTranslation();
  const selection = useAppSelector(GetSelectedItems(TreeContext.AssetHierarchy));
  const [selectedDisplayTab, setSelectedDisplayTab] = useState<TabType>("tree");

  const assetData = useAppSelector(GetObjectHierarchyDto);
  const isLoading = useAppSelector(IsObjectLoading);

  const [clickedAssetId, setClickedAssetId] = useState<number | undefined>(undefined);
  const [openAssetPanel, setOpenAssetPanel] = useState(false);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const { addObjectByIdToBasket } = useBasketActions();

  const [posterViewerVisible, setPosterViewerVisible] = useState<number | undefined>(undefined);
  const [searchValue, setSearchValue] = useState("");
  const [filtersData, setFiltersData] = useState<{
    contentFilterOptions: [];
    typeFilterOptions: ObjectType[];
    subTypeFilterOptions: string[];
    classFilterOptions: ObjectClass[];
    statusFilterOptions: ObjectStatus[];
  }>({
    contentFilterOptions: [],
    typeFilterOptions: [],
    subTypeFilterOptions: [],
    classFilterOptions: [],
    statusFilterOptions: [],
  });

  const metadata: DbMetadataFieldInfos[] = cacheService.GetMetadata();

  // Data grid
  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        minWidth: CellSize.small,
        flex: CellSize.small,
        renderCell: (data: any) => (
          <Link
            color="primary.text"
            underline="hover"
            component={RouterLink}
            to={`${routePaths.asset.path}/${data.id}`}
          >
            {data.value}
          </Link>
        ),
      },
      {
        field: "type",
        headerName: t("common:type"),
        type: "singleSelect",
        valueOptions: filtersData.typeFilterOptions,
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        renderCell: (data: any) => <AssetTypeFromObjectChip obj={data.row} />,
      },
      {
        field: "format",
        headerName: t("common:format"),
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        renderCell: (data: any) => {
          let format = GetMetadataByTagName(data.row, "techCodec", metadata)?.value.replace("_", " ");
          if (format === undefined) format = "Undefined";
          return <Chip label={format} color="default" />;
        },
      },
      {
        field: "classification",
        headerName: t("common:class"),
        type: "singleSelect",
        valueOptions: filtersData.classFilterOptions,
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        renderCell: (data: any) => <AssetClassChip label={data.value} classification={data.value} />,
      },
      {
        field: "name",
        headerName: t("common:name"),
        minWidth: CellSize.xxlarge,
        flex: CellSize.xxlarge,
        renderCell: (data: any) => <TableCellLongTypography data={data.value} />,
      },
      {
        field: "status",
        headerName: t("common:status"),
        minWidth: CellSize.medium,
        flex: CellSize.medium,
        type: "singleSelect",
        valueOptions: filtersData.statusFilterOptions,
        renderCell: (data: any) => <AssetStatus status={data.value} />,
      },
      {
        field: "actions",
        headerName: "",
        minWidth: CellSize.actionsDefault,
        flex: CellSize.actionsDefault,
        type: "actions",
        getActions: (params: GridRowParams) => [
          <GridActionsCellItem
            icon={<ShoppingBagOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
            label={t("common:addToBasket")}
            onClick={() => addObjectByIdToBasket(params?.row?.id)}
          />,
          <GridActionsCellItem
            icon={<VisibilityIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
            label={t("common:preview")}
            onClick={() => {
              if (params?.row?.type !== "Image") {
                if (!openModal) return;

                openModal<ModalPreviewProps>(ModalPreview, {
                  objectId: Number(params?.row?.id),
                  type: params?.row?.type,
                  closeModal: () => {},
                });
              } else {
                setPosterViewerVisible(params?.row?.id);
              }
            }}
            disabled={
              (params?.row?.type !== "Video" && params?.row?.type !== "Audio" && params?.row?.type !== "Image") ||
              (params?.row?.status !== "Accepted" &&
                params?.row?.status !== "Rejected" &&
                params?.row?.status !== "ToBeQCed")
            }
            showInMenu
          />,
        ],
      },
    ],
    [filtersData]
  );

  // Asset panel
  const hideAssetPanel = () => {
    setOpenAssetPanel(false);
  };

  const showAssetPanel = (e: GridRowParams) => {
    setClickedAssetId(e.row.id);
    setOpenAssetPanel(true);
  };

  useEffect(() => {
    // Waiting for redux store
    if (!assetData) return;

    if (assetData.graph && assetData.graph.childObjects) {
      setFiltersData((prevState) => {
        return {
          ...prevState,
          typeFilterOptions: assetData.graph
            .childObjects!.flatMap((x) => x.type)
            .filter(Distinct)
            .sort(),
          subTypeFilterOptions: assetData.graph
            .childObjects!.flatMap((x) => GetMetadataByTagName(x, "contentSubType", metadata)?.value)
            .filter(Distinct)
            .sort(),
          classFilterOptions: assetData.graph
            .childObjects!.filter((x) => x.classification !== null)
            .flatMap((x) => x.classification!)
            .filter(Distinct)
            .sort(),
          statusFilterOptions: assetData.graph
            .childObjects!.flatMap((x) => x.status)
            .filter(Distinct)
            .sort(),
        };
      });
    }
  }, [isLoading, assetData]);

  return (
    <Stack sx={{ py: 2, minHeight: "calc(100vh - 129px)" }}>
      {!assetData || isLoading ? (
        <TableSkeleton />
      ) : (
        <>
          <Box>
            <Card
              sx={{
                borderRadius: 1.5,
                p: 1.5,
                pr: 3,
                display: "flex",
                alignItems: "center",
              }}
            >
              <AssetThumbnail type={assetData.graph.type} />
              <Stack flex={1} ml={1.5}>
                <Typography fontWeight={500}>#{assetData.graph.id}</Typography>
                <Typography color="text.tier">{assetData.graph.name}</Typography>
                <Typography color="text.tier">
                  {t("component:body.numberOfChildren", {
                    count: assetData.graph.childObjects?.length || 0,
                  })}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center">
                {assetData.graph.childObjects?.length && (
                  <OutlinedInput
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    endAdornment={<SearchIcon color="primary" />}
                    placeholder={t("field:placeholder.searchInTab", {
                      tab: selectedDisplayTab,
                    })}
                  />
                )}
                <ToggleButtonGroup
                  exclusive
                  value={selectedDisplayTab}
                  aria-label="Treeview"
                  size="small"
                  onChange={(e, value) => setSelectedDisplayTab(value)}
                  sx={{ ml: 2 }}
                >
                  <ToggleButton value="tree" aria-label="Tree view">
                    <AccountTreeOutlinedIcon />
                  </ToggleButton>
                  <ToggleButton value="table" aria-label="List view">
                    <TableRowsOutlinedIcon />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Card>
          </Box>
          <Stack sx={{ flex: "1 1 0" }}>
            {selectedDisplayTab === "tree" && (
              <Grid container sx={{ mt: 2 }} columnSpacing={5} position="relative">
                <Grid size={8}>
                  <AssetTreeView
                    rootObjectId={assetData.graph.id}
                    context={TreeContext.AssetHierarchy}
                    multiselect
                    hideRoot
                    searchTerm={searchValue}
                  />
                </Grid>
                <Grid size={4} pt={0} pb={3}>
                  <Box position="sticky" height="100%" top={{ top: "16px", maxHeight: "calc(100vh - 250px)" }}>
                    <TreeViewActions selection={selection} graph={assetData.graph} />
                  </Box>
                </Grid>
              </Grid>
            )}
            {selectedDisplayTab === "table" && assetData.graph.childObjects && (
              <>
                <DataTable
                  tableKey={TableUuids.ASSET_SINGLE_HIERARCHY}
                  rows={assetData.graph.childObjects}
                  columns={columns}
                  getRowHeight={() => "auto"}
                  getEstimatedRowHeight={() => 200}
                  checkboxSelection={false}
                  disableRowSelectionOnClick
                  disableColumnSelector={true}
                  disableMultipleRowSelection
                  pagination={true}
                  onRowClick={showAssetPanel}
                  sx={{ mt: 4 }}
                />
                <AssetPanel
                  open={openAssetPanel}
                  onClose={hideAssetPanel}
                  closePanel={hideAssetPanel}
                  assetId={clickedAssetId}
                />
                <Viewer
                  visible={posterViewerVisible !== undefined}
                  noNavbar={true}
                  zIndex={2001}
                  onClose={() => {
                    setPosterViewerVisible(undefined);
                  }}
                  images={[
                    {
                      src: thumbnailService.GetObjectPosterUrl(posterViewerVisible, ThumbnailSize.original),
                      alt: "Image viewer #" + posterViewerVisible + " ",
                    },
                  ]}
                />
              </>
            )}
          </Stack>
        </>
      )}
    </Stack>
  );
}

export default AssetSingleHierarchy;

interface AssetHierarchyNodeProps extends HierarchyTree {
  currentObjectId: number;
  isBrowser?: boolean;
}

export const AssetHierarchyNode: FC<AssetHierarchyNodeProps> = (props) => {
  const { objectId, objectLayer, objectName, objectType, currentObjectId, isBrowser } = props;
  let isCurrent = objectId === currentObjectId;
  let isBrowserMode = isBrowser !== undefined && isBrowser === true;

  if (objectLayer === ObjectLayer.Title) {
    return (
      <Link
        underline="hover"
        variant="body1"
        to={`${routePaths.title.path}/${objectId}`}
        component={RouterLink}
        color="text.primary"
      >
        {objectName}
      </Link>
    );
  } else if (
    (objectLayer === ObjectLayer.Content ||
      objectLayer === ObjectLayer.Group ||
      objectLayer === ObjectLayer.Head ||
      objectLayer === ObjectLayer.SubContent) &&
    !isBrowserMode
  ) {
    return (
      <Link
        underline="hover"
        variant="body1"
        to={`${isBrowserMode ? routePaths.drive.path : routePaths.asset.path}/${objectId}`}
        component={RouterLink}
        color="text.tier"
      >
        {objectName}
      </Link>
    );
  } else {
    return (
      <Link
        underline={isCurrent ? "none" : "hover"}
        variant="body1"
        color={isCurrent ? "text.primary" : "text.tier"}
        to={`${isBrowserMode ? routePaths.drive.path : routePaths.asset.path}/${objectId}`}
        component={RouterLink}
      >
        {objectType && <AssetIcon fontSize="small" sx={{ mr: 0.5 }} type={objectType} colored />}
        {isCurrent && !isBrowserMode ? `#${objectId}` : objectName}
      </Link>
    );
  }
};
